import React from 'react';

type CreateContextResult<TData extends {} | null> = {
    useContext: () => TData;
    Provider: React.Provider<TData | undefined>;
    Consumer: React.Consumer<TData | undefined>;
};

/**
 * Создание комплекта "провайдер+хук"
 * @param contextName название контекста для сообщения об ошибке
 * @param contextDefaultValue дефолтное значение
 * @returns комплект
 *
 * @example
 * const SomeContextWithoutDefaultValue = createProtectedContext()
 *
 * function ParentOfSomeComponentWithoutProvider() {
 *   return <SomeComponent /> // error
 * }
 *
 * function ParentOfSomeComponentWithProvider() {
 *   return (
 *     <SomeContextWithoutDefaultValue.Provider value={'someValue'} >
 *       <SomeComponent /> // ok
 *      </SomeContextWithoutDefaultValue>
 *     )
 * }
 *
 * function SomeComponent() {
 *   const someContextInitValue = SomeContext.useContext()
 *   ...
 * }
 *
 * @example
 * const SomeContext = createProtectedContext('SomeContext', someContextInitValue)
 *
 * function ParentOfSomeComponent() {
 *   return <SomeComponent /> // ok
 * }
 *
 * function SomeComponent() {
 *   const someContextInitValue = SomeContext.useContext()
 * }
 */
export function createProtectedContext<TData extends {} | null>(
    contextName = 'Context',
    contextDefaultValue?: TData,
): CreateContextResult<TData> {
    const context = React.createContext(contextDefaultValue);

    return { useContext, Provider: context.Provider, Consumer: context.Consumer };

    function useContext(): TData {
        const contextValue = React.useContext(context);

        if (contextValue === undefined) {
            throw new Error(`Component must be used inside ${contextName}.Provider with a value`);
        }

        return contextValue;
    }
}
